import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type { Invoice, InvoiceListParams, InvoicePayload } from '@/libs/interfaces'
import { invoicesApi } from '@/services/api'

interface HookParams<T> {
  params: T
  enabled?: boolean
}

export function useInvoices({ params, enabled }: HookParams<InvoiceListParams>) {
  return useQuery<Invoice[]>({
    queryKey: QUERY_KEYS.invoices.list(params),
    queryFn: () => invoicesApi.list(params),
    enabled: (enabled ?? true) && !!params.orgID && !!params.storeID,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useInvoiceByID({
  params,
  enabled,
}: HookParams<{ orgID: string; storeID: string; billID: string }>) {
  return useQuery<Invoice>({
    queryKey: QUERY_KEYS.invoices.id(params.billID),
    queryFn: () => invoicesApi.getByID(params),
    enabled: (enabled ?? true) && !!params.orgID && !!params.storeID && !!params.billID,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useInfiniteInvoices({ params, enabled }: HookParams<InvoiceListParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.invoices.infinite(params),
    queryFn: ({ pageParam }) =>
      invoicesApi.listCursor({
        ...params,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next || undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev || undefined,
    enabled: (enabled ?? true) && !!params.orgID && !!params.storeID,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useCreateInvoice() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({
      orgID,
      storeID,
      payload,
    }: {
      orgID: string
      storeID: string
      payload: InvoicePayload
    }) => invoicesApi.create({ orgID, storeID }, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useUpdateInvoice() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({
      orgID,
      storeID,
      billID,
      payload,
    }: {
      orgID: string
      storeID: string
      billID: string
      payload: Partial<InvoicePayload>
    }) => invoicesApi.update({ orgID, storeID, billID }, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useDeleteInvoice() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (params: { orgID: string; storeID: string; billID: string }) =>
      invoicesApi.delete(params),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export type { Invoice, InvoicePayload }
